// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Centralized route permission map (Sprint 10.8.0 — F-C3).
 *
 * Maps sidebar sections and route path prefixes to the gating permission
 * required to view/access them. Both AdminLayout (sidebar filtering) and
 * App.tsx (route guards) consume this same map.
 *
 * Design rules:
 *  - Sidebar sections require the module's primary _READ permission.
 *  - Dashboard, Profile, and Help Center are always visible (no entry here).
 *  - The dev login has ALL permissions so RBAC changes do not affect dev testing.
 */
import { Permission } from '@vn-grc/shared';

// ---------------------------------------------------------------------------
// Sidebar section → gating permission
// ---------------------------------------------------------------------------

/**
 * Maps sidebar section labels (the i18n key path used in AdminLayout) to the
 * permission required to display that section. Sections not listed here are
 * always visible (Dashboard, Profile, Help Center).
 */
export const SIDEBAR_PERMISSION_MAP: Record<string, string> = {
  // Label key used as object identity — must match the label expression in AdminLayout menuItems
  rights_requests: Permission.CASE_READ,
  data_mapping: Permission.DM_SYSTEM_READ,
  governance: Permission.GOV_VENDOR_READ,
  consent: Permission.CONSENT_POLICY_READ,
  consent_properties: Permission.CONSENT_PROPERTY_READ,
  assessments: Permission.DPIA_READ,
  classification: Permission.TAXONOMY_READ,
  monitoring: Permission.PROGRAM_READ,
  incidents: Permission.INCIDENT_READ,
  connectors: Permission.CONNECTOR_READ,
  vendor_risk: Permission.VENDOR_RISK_CONFIG_READ,
  deletion_orchestration: Permission.DELETION_ORCHESTRATION_READ,
  discovery: Permission.DISCOVERY_READ,
  incident_ops: Permission.INCIDENT_OPS_PLAYBOOK_LINK_READ,
  program_governance: Permission.MATURITY_MODEL_READ,
  access_accountability: Permission.ACCESS_LOG_READ,
  onboarding: Permission.ONBOARDING_WIZARD_READ,
  localization: Permission.MARKET_PACK_READ,
  partner_workspace: Permission.PARTNER_ORG_READ,
  task_management: Permission.TM_TASK_READ,
  // Sprint 44.3.0 B6 — Platform parent-section gate weakened from
  // PLATFORM_USERS_READ to PLATFORM_FILES_BROWSE so that case_manager
  // (seeded with platform:files:browse + detail:read + metrics:read at
  // seed-roles.ts:667-669) can reach Platform via nav and see the Files
  // sub-section. Each Platform child sub-section still carries its own
  // finer-grained requiredPermission (see AdminLayout.tsx:1348+).
  platform: Permission.PLATFORM_FILES_BROWSE,
  compliance_forms: Permission.FORMS_TEMPLATE_READ,
  org_groups: Permission.GROUP_READ,
  privacy_scanner: Permission.PRIVACY_SCANNER_READ,
  audit_trail: Permission.AUDIT_EVENTS_READ,
  data_subjects: Permission.DATA_SUBJECT_READ,
  // Sprint 47.5.2 — Forseti AI Legal Advisor (Projects module).
  forseti: Permission.AI_PROJECTS_READ,
};

// ---------------------------------------------------------------------------
// Route prefix → gating permission
// ---------------------------------------------------------------------------

/**
 * Maps route path prefixes (relative to /admin/) to the permission required
 * for access. Checked by PermissionGate in App.tsx. Order matters — first
 * match wins, so more specific prefixes must come before broader ones.
 *
 * Routes not listed here are always accessible to authenticated users
 * (e.g. /admin, /admin/profile, /admin/help).
 */
export const ROUTE_PERMISSION_MAP: Array<{ prefix: string; permission: string }> = [
  // Rights Requests & Templates
  { prefix: 'rights-requests', permission: Permission.CASE_READ },
  // Sprint 43.5.1 — `/admin/templates` removed; response templates now live
  // under the rights-requests prefix (/admin/rights-requests/response-templates).

  // Audit Trail — `/admin/audit/advisor-lineage` is an alias of
  // `/admin/forseti/audit-lineage` and shares the same gate (AI_ADVISOR_CHAT).
  { prefix: 'audit/advisor-lineage', permission: Permission.AI_ADVISOR_CHAT },
  { prefix: 'audit', permission: Permission.AUDIT_EVENTS_READ },

  // Settings (top-level legacy path)
  { prefix: 'settings', permission: Permission.PLATFORM_SETTINGS_READ },

  // Platform: Users, Roles
  { prefix: 'users', permission: Permission.PLATFORM_USERS_READ },
  { prefix: 'roles', permission: Permission.PLATFORM_USERS_READ },

  // Platform: Security
  { prefix: 'security', permission: Permission.PLATFORM_SECURITY_READ },

  // Platform: Access Reviews, Attestation, SoD
  { prefix: 'access-reviews', permission: Permission.PLATFORM_ACCESS_REVIEW_READ },
  { prefix: 'attestation-schedules', permission: Permission.PLATFORM_ATTESTATION_READ },
  { prefix: 'sod-rules', permission: Permission.PLATFORM_SOD_READ },

  // Data Mapping
  { prefix: 'data-mapping', permission: Permission.DM_SYSTEM_READ },

  // Governance
  { prefix: 'governance', permission: Permission.GOV_VENDOR_READ },

  // Consent
  { prefix: 'consent', permission: Permission.CONSENT_POLICY_READ },

  // Consent Properties
  { prefix: 'consent-properties', permission: Permission.CONSENT_PROPERTY_READ },

  // Assessments
  { prefix: 'assessments', permission: Permission.DPIA_READ },

  // Classification
  { prefix: 'classification', permission: Permission.TAXONOMY_READ },

  // Monitoring
  { prefix: 'monitoring', permission: Permission.PROGRAM_READ },

  // Incidents
  { prefix: 'incidents', permission: Permission.INCIDENT_READ },

  // Connectors
  { prefix: 'connectors', permission: Permission.CONNECTOR_READ },

  // Vendor Risk
  { prefix: 'vendor-risk', permission: Permission.VENDOR_RISK_CONFIG_READ },

  // Sprint 47.5.8.1 — Contracts (first-class module).
  { prefix: 'contracts', permission: Permission.VENDOR_CONTRACT_READ },

  // Deletion Orchestration
  { prefix: 'deletion-orchestration', permission: Permission.DELETION_ORCHESTRATION_READ },

  // Discovery
  { prefix: 'discovery', permission: Permission.DISCOVERY_READ },

  // Incident Ops
  { prefix: 'incident-ops', permission: Permission.INCIDENT_OPS_PLAYBOOK_LINK_READ },

  // Program Governance
  { prefix: 'program-governance', permission: Permission.MATURITY_MODEL_READ },

  // Access Accountability
  { prefix: 'access-accountability', permission: Permission.ACCESS_LOG_READ },

  // Help FAQ Editor (more specific than onboarding, must come first)
  { prefix: 'onboarding/help/faq-editor', permission: Permission.HELP_FAQ_EDIT },

  // Onboarding
  { prefix: 'onboarding', permission: Permission.ONBOARDING_WIZARD_READ },

  // Localization
  { prefix: 'localization', permission: Permission.MARKET_PACK_READ },

  // Partner Marketplace
  { prefix: 'partner-marketplace', permission: Permission.PARTNER_ORG_READ },

  // Task Management
  { prefix: 'tasks', permission: Permission.TM_TASK_READ },

  // Platform (module config, settings, webhooks, etc.)
  { prefix: 'platform', permission: Permission.PLATFORM_SETTINGS_READ },

  // Compliance Forms
  { prefix: 'forms', permission: Permission.FORMS_TEMPLATE_READ },

  // Org Groups
  { prefix: 'org-groups', permission: Permission.GROUP_READ },

  // Privacy Scanner
  { prefix: 'privacy-scanner', permission: Permission.PRIVACY_SCANNER_READ },

  // Data Subjects
  { prefix: 'data-subjects', permission: Permission.DATA_SUBJECT_READ },

  // Sprint 47.5.2 — Forseti AI Legal Advisor.
  // `/admin/forseti/usage` is gated by AI_USAGE_VIEW; the projects pages
  // by AI_PROJECTS_READ. Order matters — narrower prefix first.
  { prefix: 'forseti/usage', permission: Permission.AI_PROVIDER_VIEW_USAGE },
  { prefix: 'forseti/document-review', permission: Permission.AI_PROJECTS_READ },
  { prefix: 'forseti/projects', permission: Permission.AI_PROJECTS_READ },
  { prefix: 'forseti/workflows', permission: Permission.AI_WORKFLOWS_READ },
  { prefix: 'forseti/tabular-reviews', permission: Permission.AI_TABULAR_REVIEWS_READ },
  { prefix: 'forseti/audit-lineage', permission: Permission.AI_ADVISOR_CHAT },
  // Sprint 47.5.4a — Forseti chat shell.
  { prefix: 'forseti/chats', permission: Permission.AI_ADVISOR_CHAT },
  { prefix: 'forseti', permission: Permission.AI_ADVISOR_CHAT },
  { prefix: 'ai-advisor', permission: Permission.AI_ADVISOR_CHAT },
];

/**
 * Look up the gating permission for a given route path (relative to /admin/).
 * Returns undefined if the route is always accessible.
 */
export function getRoutePermission(relativePath: string): string | undefined {
  for (const entry of ROUTE_PERMISSION_MAP) {
    if (relativePath === entry.prefix || relativePath.startsWith(entry.prefix + '/')) {
      return entry.permission;
    }
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Route prefix → ModuleId (for module-config visibility gating)
// ---------------------------------------------------------------------------

/**
 * Maps route path prefixes to the ModuleId that must be enabled for the route
 * to be accessible. Routes not listed here are always accessible regardless of
 * module configuration (e.g. dashboard, profile, help, platform settings).
 *
 * Sprint 15.6.0 — Module configuration sidebar + route gating.
 */
export const ROUTE_MODULE_MAP: Array<{ prefix: string; moduleId: string }> = [
  { prefix: 'rights-requests', moduleId: 'rights_requests' },
  // Sprint 43.5.1 — `/admin/templates` route removed.
  { prefix: 'audit', moduleId: 'audit_trail' },
  { prefix: 'data-mapping', moduleId: 'data_mapping' },
  { prefix: 'governance', moduleId: 'governance' },
  { prefix: 'consent-properties', moduleId: 'consent_properties' },
  { prefix: 'consent', moduleId: 'consent' },
  { prefix: 'assessments', moduleId: 'assessments' },
  { prefix: 'classification', moduleId: 'classification' },
  { prefix: 'monitoring', moduleId: 'monitoring' },
  { prefix: 'incidents', moduleId: 'incidents' },
  { prefix: 'connectors', moduleId: 'connectors' },
  { prefix: 'vendor-risk', moduleId: 'vendor_risk' },
  { prefix: 'contracts', moduleId: 'contracts' },
  { prefix: 'deletion-orchestration', moduleId: 'deletion_orchestration' },
  { prefix: 'discovery', moduleId: 'discovery' },
  { prefix: 'incident-ops', moduleId: 'incident_ops' },
  { prefix: 'program-governance', moduleId: 'program_governance' },
  { prefix: 'access-accountability', moduleId: 'access_accountability' },
  { prefix: 'onboarding', moduleId: 'onboarding' },
  { prefix: 'localization', moduleId: 'localization' },
  { prefix: 'partner-marketplace', moduleId: 'partner_workspace' },
  { prefix: 'forms', moduleId: 'compliance_forms' },
  { prefix: 'tasks', moduleId: 'task_management' },
  { prefix: 'organizations', moduleId: 'organizations' },
  { prefix: 'org-groups', moduleId: 'organizations' },
  { prefix: 'intake-configs', moduleId: 'organizations' },
  { prefix: 'privacy-scanner', moduleId: 'privacy_scanner' },
  { prefix: 'data-subjects', moduleId: 'data_subjects' },
];

/**
 * Look up the ModuleId for a given route path (relative to /admin/).
 * Returns undefined if the route has no module gating.
 */
export function getRouteModule(relativePath: string): string | undefined {
  for (const entry of ROUTE_MODULE_MAP) {
    if (relativePath === entry.prefix || relativePath.startsWith(entry.prefix + '/')) {
      return entry.moduleId;
    }
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Sidebar section → ModuleId (for sidebar visibility gating)
// ---------------------------------------------------------------------------

/**
 * Maps sidebar section keys to ModuleId values. Sections not listed here are
 * always visible regardless of module configuration (dashboard, platform, help).
 */
export const SIDEBAR_MODULE_MAP: Record<string, string> = {
  rights_requests: 'rights_requests',
  data_mapping: 'data_mapping',
  governance: 'governance',
  consent: 'consent',
  consent_properties: 'consent_properties',
  assessments: 'assessments',
  classification: 'classification',
  monitoring: 'monitoring',
  incidents: 'incidents',
  connectors: 'connectors',
  vendor_risk: 'vendor_risk',
  deletion_orchestration: 'deletion_orchestration',
  discovery: 'discovery',
  incident_ops: 'incident_ops',
  program_governance: 'program_governance',
  access_accountability: 'access_accountability',
  onboarding: 'onboarding',
  localization: 'localization',
  partner_workspace: 'partner_workspace',
  compliance_forms: 'compliance_forms',
  privacy_scanner: 'privacy_scanner',
  audit_trail: 'audit_trail',
  task_management: 'task_management',
  organizations: 'organizations',
  data_subjects: 'data_subjects',
  // Sprint 47.5.2 — Forseti AI Legal Advisor.
  forseti: 'forseti',
};
